import {
    WESOG_STORE_PARAMS,
    STORE_SEARCHER_FILTERS,
    TUTORIAL_TIP_MESSAGES_TYPES,
    WESOG_STORE_CONFIGURATION_PARAMS,
    WESOG_SESSION_STORE_PARAMS,
} from '@core/constants/STORE';
import { WESOG_CENTER_STORE, CURRENT_TAB } from '@core/constants/CONSTANTS';
import {
    DEFAULT_PER_PAGE_ELEMENTS,
    DEFAULT_ZOOM_VALUE,
    DEFAULT_OPEN_LINKS,
    DEFAULT_DARK_MODE_VALUE,
    DEFAULT_PAGINATION_VALUE,
} from '@core/constants/DEFAULT';
import { createUserObj } from '@core/utils/wesog-user';
import { getSessionStoreValue, updateSessionStoreValue } from './sessionStore';
import { SEARCHER_OPTIONS, SEARCHER_TYPES } from '@core/constants/SEARCHER';

/**
 * Create the store in local Storage.
 * @returns {void}
 */
export const createStore = () => {
    localStorage.setItem(
        WESOG_CENTER_STORE,
        JSON.stringify({
            [WESOG_STORE_PARAMS.USER]: createUserObj(),
            [WESOG_STORE_PARAMS.CURRENT_LANGUAGE]: '',
            [WESOG_STORE_PARAMS.DEFAULT_HOME_REPORT_VIEW]: '',
            [WESOG_STORE_PARAMS.NAV_LINKS_LAST_VISITED]: '',
            [WESOG_STORE_PARAMS.FILTER_STATES_HISTORY]: [],
            [WESOG_STORE_PARAMS.TUTORIAL_TIPS]: {
                [TUTORIAL_TIP_MESSAGES_TYPES.SEARCH_INPUT]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.AI_SWITCHER]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.IMAGE_SEARCHER]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.DEFAULT_REPORT]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.WIDGETS]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.CHARACTERS]: true,
                [TUTORIAL_TIP_MESSAGES_TYPES.INSTALLED_EXTENSION]: true,
            },
            [WESOG_STORE_PARAMS.CONFIGURATION]: {
                [WESOG_STORE_CONFIGURATION_PARAMS.PER_PAGE]: DEFAULT_PER_PAGE_ELEMENTS,
                [WESOG_STORE_CONFIGURATION_PARAMS.OPEN_LINKS_IN_NEW_TAB]:
                    DEFAULT_OPEN_LINKS,
                [WESOG_STORE_CONFIGURATION_PARAMS.ZOOM]: DEFAULT_ZOOM_VALUE,
                [WESOG_STORE_CONFIGURATION_PARAMS.DARK_MODE]: DEFAULT_DARK_MODE_VALUE,
                [WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION]: DEFAULT_PAGINATION_VALUE,
                [WESOG_STORE_CONFIGURATION_PARAMS.DESCRIPTION_ACTIVE]: false,
            },
        }),
    );
};

/**
 * Get the store from local Storage.
 * @returns {{
 *  user: Object,
 * }} The store.
 */
export const getStore = () => {
    let /** @type {Object} */ store = JSON.parse(
            localStorage.getItem(WESOG_CENTER_STORE),
        );

    if (!store) {
        createStore();

        store = JSON.parse(localStorage.getItem(WESOG_CENTER_STORE));
    }

    return store;
};

/**
 * Get a value from the store.
 * @param {String} key - The key.
 * @returns {any} - The value.
 */
export const getStoreValue = (key) => {
    const /** @type {Object} */ store = getStore();

    return store ? store[key] : undefined;
};

/**
 * Update the store in local Storage.
 * @param {Object} store - The store.
 * @returns {void}
 */
export const updateStore = (store) => {
    localStorage.setItem(WESOG_CENTER_STORE, JSON.stringify(store));
};

/**
 * Update a value in the store.
 * @param {String} key
 * @param {any} value
 * @returns {void}
 */
export const updateStoreValue = (key, value) => {
    const /** @type {Object} */ store = getStore();
    store[key] = value;

    updateStore(store);
};

/**
 * Remove the store from local Storage.
 * @returns {void}
 */
export const removeStore = () => {
    localStorage.removeItem(WESOG_CENTER_STORE);
};

/**
 * Remove a value from the store.
 * @param {String} key - The key.
 * @returns {void}
 */
export const removeStoreValue = (key) => {
    const /** @type {Object} */ store = getStore();
    store[key] = '';

    updateStore(store);
};

/**
 * Check if the store has a value.
 * @param {String} key - The key.
 * @returns {boolean} - True if the store has the value.
 */
export const hasStoreValue = (key) => {
    return getStoreValue(key) !== undefined;
};

/**
 * Add a new param to the store.
 * @param {String} key - The key.
 * @param {any} value - The value.
 * @returns {void}
 */
export const addNewParamToStore = (key, value) => {
    updateStoreValue(key, value);
};

/**
 * Get the user from the store.
 * @returns {{
 *  username: string,
 *  access_token: string,
 *  expires_in: number,
 *  searcherHistory: Array<object>,
 *  lastName: string,
 *  email: string,
 *  id: string,
 *  language: string,
 *  phone: string,
 *  pseudo: string,
 *  lightbox: Array<object>,
 * }|undefined} - The user.
 */
export const getStoreUser = () => {
    const /** @type {Object} */ store = getStore();

    return store ? store[WESOG_STORE_PARAMS.USER] : undefined;
};

/**
 * Update the user in the store.
 * @param {Object} user - The user.
 * @returns {void}
 */
export const updateStoreUser = (user) => {
    const /** @type {Object} */ store = getStore();
    store[WESOG_STORE_PARAMS.USER] = user;

    updateStore(store);
};

/**
 * Remove the user from the store.
 * @returns {void}
 */
export const removeStoreUser = () => {
    const /** @type {Object} */ store = getStore();
    delete store[WESOG_STORE_PARAMS.USER];

    updateStore(store);
};

/**
 * Get the current configuration from the store.
 * @returns {object} - The current configuration.
 */
export const getStoreConfiguration = () => {
    const /** @type {Object} */ store = getStore();

    return store[WESOG_STORE_PARAMS.CONFIGURATION] || {};
};

/**
 * Update the current configuration in the store.
 * @param {object} configuration - The new configuration.
 * @returns {void}
 */
export const updateStoreConfiguration = (configuration) => {
    const /** @type {Object} */ store = getStore();
    store[WESOG_STORE_PARAMS.CONFIGURATION] = configuration;

    updateStore(store);
};

/**
 * Get the Filters state history from the store.
 * @returns {Array<object>} - The Filters state history.
 */
export const getStoreFiltersState = () => {
    const /** @type {Object} */ store = getStore();

    if (!hasStoreValue(WESOG_STORE_PARAMS.FILTER_STATES_HISTORY)) {
        addNewParamToStore(WESOG_STORE_PARAMS.FILTER_STATES_HISTORY, []);
    }

    return store[WESOG_STORE_PARAMS.FILTER_STATES_HISTORY] || [];
};

/**
 * Get a filter state by id from the store.
 * @param {string} id - The id.
 * @returns {object|undefined} - The filter state.
 */
export const getStoreFilterStateById = (id) => {
    const /** @type {Array<object>} */ filtersState = getStoreFiltersState();

    if (!filtersState) return addStoreFiltersState(id);

    return filtersState.find(
        (filterState) => filterState[STORE_SEARCHER_FILTERS.ID] === id,
    );
};

/**
 * Update the Filters state history in the store.
 * @param {string} filtersStateId - The new Filters state id.
 * @returns {void}
 */
export const addStoreFiltersState = (filterHistoryId) => {
    const /** @type {Array<object>} */ storeFiltersState = getStoreFiltersState();

    /* Add the new filters state but if we already have 5 filters state we remove the oldest one */
    if (storeFiltersState.length >= 10) {
        storeFiltersState.shift();
    }

    storeFiltersState.push({
        [STORE_SEARCHER_FILTERS.ID]: filterHistoryId,
        [STORE_SEARCHER_FILTERS.ACTIVATED_FACES]: [],
        [STORE_SEARCHER_FILTERS.DEACTIVATED_FACES]: [],
        [STORE_SEARCHER_FILTERS.ACTIVATED_FILTERS]: [],
        [STORE_SEARCHER_FILTERS.ACTIVATED_VIDEO_FILTERS]: [],
        [STORE_SEARCHER_FILTERS.ORDER_BY_VALUE]: '',
        [STORE_SEARCHER_FILTERS.PAGE_VALUE]: 0,
        [STORE_SEARCHER_FILTERS.PAGE_REPORT_VALUE]: 0,
        [STORE_SEARCHER_FILTERS.LAST_ELEMENT_SELECTED_ID]: '',
        [STORE_SEARCHER_FILTERS.SELECTED_TAB]: CURRENT_TAB.IMAGES,
    });

    updateStoreValue(WESOG_STORE_PARAMS.FILTER_STATES_HISTORY, storeFiltersState);
};

/**
 * Update the Filters state history in the store.
 * @param {string} filtersStateId - The Filters state id.
 * @param {object} filtersState - The Filters state.
 * @returns {void}
 */
export const updateStoreFiltersState = (filtersStateId, filtersState) => {
    const /** @type {Array<object>} */ storeFiltersState = getStoreFiltersState();

    const /** @type {number} */ index = storeFiltersState.findIndex(
            (filterState) => filterState.id === filtersStateId,
        );

    if (index !== -1) {
        storeFiltersState[index] = filtersState;
    }

    updateStoreValue(WESOG_STORE_PARAMS.FILTER_STATES_HISTORY, storeFiltersState);
};

/**
 * Get the Open links in new tab configuration from the store.
 * @returns {boolean} - The Open links in new tab configuration.
 */
export const getStoreConfigurationLink = () => {
    const /** @type {Object} */ configuration = getStoreConfiguration();

    return configuration[WESOG_STORE_CONFIGURATION_PARAMS.OPEN_LINKS_IN_NEW_TAB] || false;
};

/**
 * Reset the filter states history page in the store.
 * @returns {void}
 */
export const resetFilterStatesHistoryPage = () => {
    const /** @type {Object} */ filterStatesHistories = getStoreFiltersState();

    filterStatesHistories.forEach((filterState) => {
        filterState[STORE_SEARCHER_FILTERS.PAGE_VALUE] = 0;
        filterState[STORE_SEARCHER_FILTERS.PAGE_REPORT_VALUE] = 0;
    });
};

/**
 * Saves the current search query in session storage
 * @returns {void}
 */
export const saveCurrentSearchQuery = () => {
    const currentSearchOptions =
        getSessionStoreValue(WESOG_SESSION_STORE_PARAMS.CURRENT_QUERY) || {};
    const urlParams = new URLSearchParams(window.location.search);

    updateSessionStoreValue(WESOG_SESSION_STORE_PARAMS.CURRENT_QUERY, {
        ...currentSearchOptions,
        [SEARCHER_TYPES.OPTION]:
            urlParams.get(SEARCHER_TYPES.OPTION) || SEARCHER_OPTIONS.ED_IMGS,
        [SEARCHER_TYPES.AI]: urlParams.get(SEARCHER_TYPES.AI) || true,
    });
};
